import { useGlobalSettingsGetSettings } from 'api/global-settings/global-settings';
import { GlobalSettingEnum } from 'api/model';
import { useMemo } from 'react';

type GlobalSettingsData = {
  [GlobalSettingEnum.CarTransportTruckOnOffLoadingTime]: number;
  [GlobalSettingEnum.CarTransportTruckOnOffLoadingTimePerCar]: number;
  [GlobalSettingEnum.RegularFieldTesterBreakTimePer5Hours]: number;
  [GlobalSettingEnum.CarTransportTruckMaxConsecutiveWorkHoursWithoutBreak6To9Hours]: number;
  [GlobalSettingEnum.CarTransportTruckMaxConsecutiveWorkHoursWithoutBreakMoreThan9Hours]: number;
  [GlobalSettingEnum.UsersExcludedFromPlaceholderAssignmentRule]: Record<
    number,
    boolean | undefined
  >;
  [GlobalSettingEnum.CountyNamesCountedAsLargeCities]: string[];
  [GlobalSettingEnum.UsersWithAccessToInEditStopOvers]: Record<
    number,
    boolean | undefined
  >;
  [GlobalSettingEnum.UsersWithAccessToTranslation]: Record<
    number,
    boolean | undefined
  >;
  [GlobalSettingEnum.UsersWithAccessToDayPlanning]: Record<
    number,
    boolean | undefined
  >;
  [GlobalSettingEnum.UsersWithAccessToRoutePlanApply]: Record<
    number,
    boolean | undefined
  >;
  [GlobalSettingEnum.UsersWithAccessToInvoiceExtras]: Record<
    number,
    boolean | undefined
  >;
  [GlobalSettingEnum.UsersWithAccessToSharebox]: Record<
    number,
    boolean | undefined
  >;
  [GlobalSettingEnum.UsersWithAccessToRiddermarkReporting]: Record<
    number,
    boolean | undefined
  >;
  [GlobalSettingEnum.UsersWithAccessToRoutePlanningSettings]: Record<
    number,
    boolean | undefined
  >;
  [GlobalSettingEnum.HRSpeedingReportEnabled]: boolean;
  [GlobalSettingEnum.RoutePlanAbsoluteTbEnabled]: boolean;
};

const useGlobalSettings = () => {
  const getGlobalSettingsCall = useGlobalSettingsGetSettings({
    query: { staleTime: 5 * 60 * 1000 },
  });

  const parsedData = useMemo((): GlobalSettingsData => {
    const settings: GlobalSettingsData = {
      [GlobalSettingEnum.CarTransportTruckOnOffLoadingTime]: 90,
      [GlobalSettingEnum.CarTransportTruckOnOffLoadingTimePerCar]: 15,
      [GlobalSettingEnum.RegularFieldTesterBreakTimePer5Hours]: 30,
      [GlobalSettingEnum.CarTransportTruckMaxConsecutiveWorkHoursWithoutBreak6To9Hours]: 30,
      [GlobalSettingEnum.CarTransportTruckMaxConsecutiveWorkHoursWithoutBreakMoreThan9Hours]: 45,
      [GlobalSettingEnum.UsersExcludedFromPlaceholderAssignmentRule]: {},
      [GlobalSettingEnum.CountyNamesCountedAsLargeCities]: [],
      [GlobalSettingEnum.UsersWithAccessToInEditStopOvers]: {},
      [GlobalSettingEnum.UsersWithAccessToTranslation]: {},
      [GlobalSettingEnum.UsersWithAccessToDayPlanning]: {},
      [GlobalSettingEnum.UsersWithAccessToRoutePlanApply]: {},
      [GlobalSettingEnum.UsersWithAccessToInvoiceExtras]: {},
      [GlobalSettingEnum.UsersWithAccessToSharebox]: {},
      [GlobalSettingEnum.UsersWithAccessToRiddermarkReporting]: {},
      [GlobalSettingEnum.UsersWithAccessToRoutePlanningSettings]: {},
      [GlobalSettingEnum.HRSpeedingReportEnabled]: false,
      [GlobalSettingEnum.RoutePlanAbsoluteTbEnabled]: false,
    };

    if (getGlobalSettingsCall.data) {
      settings[GlobalSettingEnum.CarTransportTruckOnOffLoadingTime] = Number(
        getGlobalSettingsCall.data[
          GlobalSettingEnum.CarTransportTruckOnOffLoadingTime
        ] ?? '90'
      );
      settings[GlobalSettingEnum.CarTransportTruckOnOffLoadingTimePerCar] =
        Number(
          getGlobalSettingsCall.data[
            GlobalSettingEnum.CarTransportTruckOnOffLoadingTimePerCar
          ] ?? '15'
        );

      settings[GlobalSettingEnum.RegularFieldTesterBreakTimePer5Hours] = Number(
        getGlobalSettingsCall.data[
          GlobalSettingEnum.RegularFieldTesterBreakTimePer5Hours
        ] ?? '30'
      );
      settings[
        GlobalSettingEnum.CarTransportTruckMaxConsecutiveWorkHoursWithoutBreak6To9Hours
      ] = Number(
        getGlobalSettingsCall.data[
          GlobalSettingEnum
            .CarTransportTruckMaxConsecutiveWorkHoursWithoutBreak6To9Hours
        ] ?? '30'
      );
      settings[
        GlobalSettingEnum.CarTransportTruckMaxConsecutiveWorkHoursWithoutBreakMoreThan9Hours
      ] = Number(
        getGlobalSettingsCall.data[
          GlobalSettingEnum
            .CarTransportTruckMaxConsecutiveWorkHoursWithoutBreakMoreThan9Hours
        ] ?? '45'
      );

      try {
        const usersWithAccessToPlaceholderAssignments =
          getGlobalSettingsCall.data[
            GlobalSettingEnum.UsersExcludedFromPlaceholderAssignmentRule
          ]?.split(',') ?? [];

        settings[GlobalSettingEnum.UsersExcludedFromPlaceholderAssignmentRule] =
          usersWithAccessToPlaceholderAssignments.reduce(
            (acc, userId) => {
              acc[Number(userId)] = true;
              return acc;
            },
            {} as Record<number, boolean>
          );
      } catch (error) {
        // do nothing
      }

      try {
        const usersWithAccessToInEditStopOvers =
          getGlobalSettingsCall.data[
            GlobalSettingEnum.UsersWithAccessToInEditStopOvers
          ]?.split(',') ?? [];

        settings[GlobalSettingEnum.UsersWithAccessToInEditStopOvers] =
          usersWithAccessToInEditStopOvers.reduce(
            (acc, userId) => {
              acc[Number(userId)] = true;
              return acc;
            },
            {} as Record<number, boolean>
          );
      } catch (error) {
        // do nothing
      }

      try {
        const usersWithAccessToTranslation =
          getGlobalSettingsCall.data[
            GlobalSettingEnum.UsersWithAccessToTranslation
          ]?.split(',') ?? [];

        settings[GlobalSettingEnum.UsersWithAccessToTranslation] =
          usersWithAccessToTranslation.reduce(
            (acc, userId) => {
              acc[Number(userId)] = true;
              return acc;
            },
            {} as Record<number, boolean>
          );
      } catch (error) {
        // do nothing
      }

      try {
        const usersWithAccessToDayPlanning =
          getGlobalSettingsCall.data[
            GlobalSettingEnum.UsersWithAccessToDayPlanning
          ]?.split(',') ?? [];

        settings[GlobalSettingEnum.UsersWithAccessToDayPlanning] =
          usersWithAccessToDayPlanning.reduce(
            (acc, userId) => {
              acc[Number(userId)] = true;
              return acc;
            },
            {} as Record<number, boolean>
          );
      } catch (error) {
        // do nothing
      }

      try {
        // RM:s rapportering — /ledningsrapport och /nyckeltal-riddermark bakom samma lista.
        // Tom lista = dolt för alla, också för SuperAdmin; se GlobalSettingEnum 99.
        const usersWithAccessToRiddermarkReporting =
          getGlobalSettingsCall.data[
            GlobalSettingEnum.UsersWithAccessToRiddermarkReporting
          ]?.split(',') ?? [];

        settings[GlobalSettingEnum.UsersWithAccessToRiddermarkReporting] =
          usersWithAccessToRiddermarkReporting.reduce(
            (acc, userId) => {
              acc[Number(userId)] = true;
              return acc;
            },
            {} as Record<number, boolean>
          );
      } catch (error) {
        // do nothing
      }

      try {
        // Fliken Inställningar → Ruttplanering. Tom lista = dold för alla; se GlobalSettingEnum 116.
        const usersWithAccessToRoutePlanningSettings =
          getGlobalSettingsCall.data[
            GlobalSettingEnum.UsersWithAccessToRoutePlanningSettings
          ]?.split(',') ?? [];

        settings[GlobalSettingEnum.UsersWithAccessToRoutePlanningSettings] =
          usersWithAccessToRoutePlanningSettings.reduce(
            (acc, userId) => {
              acc[Number(userId)] = true;
              return acc;
            },
            {} as Record<number, boolean>
          );
      } catch (error) {
        // do nothing
      }

      try {
        // Vem som får verkställa och låsa uppdrag i ruttplaneringen. Att se den får alla admins.
        const usersWithAccessToRoutePlanApply =
          getGlobalSettingsCall.data[
            GlobalSettingEnum.UsersWithAccessToRoutePlanApply
          ]?.split(',') ?? [];

        settings[GlobalSettingEnum.UsersWithAccessToRoutePlanApply] =
          usersWithAccessToRoutePlanApply.reduce(
            (acc, userId) => {
              acc[Number(userId)] = true;
              return acc;
            },
            {} as Record<number, boolean>
          );
      } catch (error) {
        // do nothing
      }

      try {
        const usersWithAccessToInvoiceExtras =
          getGlobalSettingsCall.data[
            GlobalSettingEnum.UsersWithAccessToInvoiceExtras
          ]?.split(',') ?? [];

        settings[GlobalSettingEnum.UsersWithAccessToInvoiceExtras] =
          usersWithAccessToInvoiceExtras.reduce(
            (acc, userId) => {
              acc[Number(userId)] = true;
              return acc;
            },
            {} as Record<number, boolean>
          );
      } catch (error) {
        // do nothing
      }

      try {
        const usersWithAccessToSharebox =
          getGlobalSettingsCall.data[
            GlobalSettingEnum.UsersWithAccessToSharebox
          ]?.split(',') ?? [];

        settings[GlobalSettingEnum.UsersWithAccessToSharebox] =
          usersWithAccessToSharebox.reduce(
            (acc, userId) => {
              acc[Number(userId)] = true;
              return acc;
            },
            {} as Record<number, boolean>
          );
      } catch (error) {
        // do nothing
      }

      try {
        const countyNamesCountedAsLargeCities =
          getGlobalSettingsCall.data[
            GlobalSettingEnum.CountyNamesCountedAsLargeCities
          ]?.split(',') ?? [];

        settings[GlobalSettingEnum.CountyNamesCountedAsLargeCities] =
          countyNamesCountedAsLargeCities;
      } catch (error) {
        settings[GlobalSettingEnum.CountyNamesCountedAsLargeCities] = [];
        // do nothing
      }

      settings[GlobalSettingEnum.HRSpeedingReportEnabled] =
        getGlobalSettingsCall.data[
          GlobalSettingEnum.HRSpeedingReportEnabled
        ] === 'true';

      settings[GlobalSettingEnum.RoutePlanAbsoluteTbEnabled] =
        getGlobalSettingsCall.data[
          GlobalSettingEnum.RoutePlanAbsoluteTbEnabled
        ] === 'true';
    }

    return settings;
  }, [getGlobalSettingsCall.data]);

  return {
    parsedData,
    globalSettings: getGlobalSettingsCall.data ?? {},
    isLoading: getGlobalSettingsCall.isLoading,
    isError: getGlobalSettingsCall.isError,
    error: getGlobalSettingsCall.error,
    loaded: getGlobalSettingsCall.isSuccess,
  };
};

export default useGlobalSettings;
